/**
 * Integration Marketplace Types
 *
 * Types aligned with Galactus backend DTOs and UI-specific models
 * for the integrations marketplace experience.
 */

// ============================================================================
// Integration Tab / Route Constants
// ============================================================================

export enum IntegrationTab {
   Partners = "partners",
   Calendar = "calendar",
   Apps = "apps",
   CustomTools = "customTools",
   Webhooks = "webhooks",
}

export function integrationTabRoute(tab: IntegrationTab): string {
   return `/integrations/${tab}`;
}

// ============================================================================
// Galactus Backend Enums
// ============================================================================

export enum IntegrationListingStatus {
   Requested = "requested",
   Planned = "planned",
   Supported = "supported",
   Maintenance = "maintenance",
}

export enum IntegrationTag {
   Crm = "crm",
   Telephony = "telephony",
   Analytics = "analytics",
   Marketing = "marketing",
   AgencySystems = "agency_systems",
   Calendar = "calendar",
   Communications = "communications",
   Developer = "developer",
   TeamChat = "team_chat",
   QuotingRaters = "quoting_raters",
   ClientIntakeForms = "client_intake_forms",
   ReportingBi = "reporting_bi",
}

export enum ProgressStatus {
   NotStarted = "not_started",
   InProgress = "in_progress",
   Beta = "beta",
   Live = "live",
}

export enum VoteUrgency {
   Low = "low",
   Medium = "medium",
   High = "high",
}

export enum IntegrationRequestStatus {
   Pending = "pending",
   Approved = "approved",
   Merged = "merged",
   Promoted = "promoted",
   Declined = "declined",
}

// ============================================================================
// Galactus API Response Types (mirror backend DTOs)
// ============================================================================

export interface IntegrationListingResponse {
   id: string;
   key: string;
   name: string;
   tags: IntegrationTag[];
   descriptions: Record<string, string>;
   logoUrl: string;
   status: IntegrationListingStatus;
   route: string | null;
   featured: boolean;
   progressStatus: ProgressStatus;
}

export interface IntegrationListingVoteResponse {
   id: string;
   integrationId: string;
   orgId: string;
   userId: string;
   lastUpdated: string;
   useCase: string | null;
   urgency: VoteUrgency;
}

export interface IntegrationRequestResponse {
   id: string;
   name: string;
   orgId: string;
   userId: string;
   useCase: string | null;
   urgency: VoteUrgency;
   requestedAt: string;
   status: IntegrationRequestStatus;
   mergedIntoRequestId: string | null;
   promotedToListingId: string | null;
}

// ============================================================================
// Galactus API Request Types
// ============================================================================

export interface IntegrationListingVoteRequest {
   integrationId: string;
   useCase?: string | null;
   urgency?: VoteUrgency;
}

export interface IntegrationRequestRequest {
   name: string;
   useCase: string | null;
   urgency: VoteUrgency;
}

export interface IntegrationListingRequest {
   key: string;
   name: string;
   tags: IntegrationTag[];
   descriptions: Record<string, string>;
   logoUrl: string;
   status: IntegrationListingStatus;
   route: string | null;
   featured: boolean;
   progressStatus: ProgressStatus;
}

export interface PromoteIntegrationRequestRequest {
   key: string;
   descriptions: Record<string, string>;
   logoUrl: string;
}

export interface MergeIntegrationRequestsRequest {
   primaryRequestId: string;
   secondaryRequestIds: string[];
}

// ============================================================================
// Paginated Response
// ============================================================================

export interface PaginatedResult<T> {
   items: T[];
   totalCount: number;
   pageNumber: number;
   pageSize: number;
}

// ============================================================================
// UI-Specific Types
// ============================================================================

/**
 * Marketplace filter type for the sidebar. Combines actual IntegrationTag
 * values with two UI-only pseudo-filters.
 */
export const MARKETPLACE_FILTER_ALL = "all" as const;
export const MARKETPLACE_FILTER_FEATURED = "featured" as const;
export type MarketplaceFilter =
   | IntegrationTag
   | typeof MARKETPLACE_FILTER_ALL
   | typeof MARKETPLACE_FILTER_FEATURED;

/**
 * Unified integration model for the marketplace grid.
 * Derived from IntegrationListingResponse + connection state.
 */
export interface UnifiedIntegration {
   id: string;
   key?: string;
   name: string;
   category?: IntegrationTag;
   description: string;
   logoUrl: string;
   status: IntegrationListingStatus;
   progressStatus: ProgressStatus;
   votes?: number;
   connected: boolean;
   connectionAvailable: boolean;
   route?: string;
   featured: boolean;
   tags: IntegrationTag[];
}

export type ActionResult<T> = {success: true; data: T} | {success: false; error: string};
